import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import { classMap } from 'lit-html/directives/class-map';
import { KatLitElement, register } from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './hero.lit.scss';
import getString from './strings';

/**
 * @component {kat-hero} KatHero The `Hero` component is a top-of-page component that standardizing the display of information about a single entity  (an item or order, for example). Use it as an alternative to the page header when the page is specifically focused on a single entity. It is commonly used with the Entity Detail Blueprint.
 * @example KatHero {"header": "Header", "subheader": "Subheader", "price": "29.95", "content": "<kat-hero-list-actions><kat-action-bar><kat-button variant=\"secondary\">Back to list</kat-button><kat-button variant=\"tertiary\">Add</kat-button></kat-action-bar></kat-hero-list-actions><kat-hero-image><img alt=\"detail image\" src=\"https://placehold.co/319x340\"/></kat-hero-image><kat-hero-status><kat-statusindicator label=\"Active\" variant=\"success\"></kat-statusindicator><span>In Stock</span></kat-hero-status><kat-hero-details><kat-description-list><dt>ASIN</dt><dd>B088QSDB7S</dd><dt>SKU</dt><dd>CPH0501</dd><dt>UPC</dt><dd>100-98671828764891-890</dd></kat-description-list></kat-hero-details><kat-hero-extended-info>Additional details inside expander</kat-hero-extended-info><kat-hero-actions><kat-button>Primary Action</kat-button><kat-button variant=\"secondary\">Secondary Action</kat-button><kat-button variant=\"tertiary\">Tertiary Action</kat-button></kat-hero-actions><kat-hero-section-header-group><kat-section-header-group><kat-section-header action-icon=\"info\" action-label=\"Action\" header=\"Section\" status=\"Status\"><p>Section Content</p></kat-section-header><kat-section-header action-icon=\"info\" action-label=\"Action\" header=\"Section\" status=\"Status\"><p>Section Content</p></kat-section-header><kat-section-header action-icon=\"info\" action-label=\"Action\" header=\"Section\" status=\"Status\"><p>Section Content</p></kat-section-header><kat-section-header action-icon=\"info\" action-label=\"Action\" header=\"Section\" status=\"Status\"><p>Section Content</p></kat-section-header><kat-section-header action-icon=\"info\" action-label=\"Action\" header=\"Section\" status=\"Status\"><p>Section Content</p></kat-section-header><kat-section-header action-icon=\"info\" action-label=\"Action\" header=\"Section\" status=\"Status\"><p>Section Content</p></kat-section-header></kat-section-header-group></kat-hero-section-header-group>" }
 * @subcomponent ./hero-list-actions.ts
 * @subcomponent ./hero-image.ts
 * @subcomponent ./hero-status.ts
 * @subcomponent ./hero-details.ts
 * @subcomponent ./hero-extended-info.ts
 * @subcomponent ./hero-actions.ts
 * @subcomponent ./hero-section-header-group.ts
 * @a11y {keyboard}
 * @a11y {contrast}
 * @a11y {sr}
 */
@register('kat-hero')
export class KatHero extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  // Mapping of a component/slot name to their allowed components
  static slotMapping = {
    listActions: {
      name: 'kat-hero-list-actions',
      allowed: ['kat-action-bar'],
    },
    image: {
      name: 'kat-hero-image',
      allowed: [],
    },
    status: {
      name: 'kat-hero-status',
      allowed: [],
    },
    details: {
      name: 'kat-hero-details',
      allowed: [],
    },
    extendedInfo: {
      name: 'kat-hero-extended-info',
      allowed: [],
    },
    actions: {
      name: 'kat-hero-actions',
      allowed: ['kat-button'],
    },
    sectionHeaderGroup: {
      name: 'kat-hero-section-header-group',
      allowed: ['kat-section-header-group'],
    },
  };

  /** Force the main layout to be vertical regardless of viewport width. */
  @property()
  vertical?: boolean;

  /** Header that will be displayed. */
  @property()
  header?: string;

  /** Subeader that will be displayed. */
  @property()
  subheader?: string;

  /** Price for the currently entity. */
  @property()
  price?: string;

  /** Label to use the for the extended info label.  Defaults to translated "More details." */
  @property({ attribute: 'extended-info-label' })
  extendedInfoLabel?: string;

  /**
   * Locale to identify the language used in fixed labels.
   * Use this property/attribute to override the automatic language detection.
   * @type {string}
   */
  @property()
  locale?: string;

  render() {
    const slots = checkSlots(this);
    const hasImage = !!slots[KatHero.slotMapping.image.name];
    const hasActions = !!slots[KatHero.slotMapping.actions.name];

    const renderedHeader = this.header
      ? html`<div class="header">${this.header}</div>`
      : nothing;
    const renderedSubheader = this.subheader
      ? html`<div class="subheader">${this.subheader}</div>`
      : nothing;
    const renderedPrice = this.price
      ? html`<div class="price">${this.price}</div>`
      : nothing;
    const renderedImage = hasImage
      ? html`<div class="image">
          <slot name="${KatHero.slotMapping.image.name}"></slot>
        </div>`
      : nothing;
    const renderedExtendedInfo = slots[KatHero.slotMapping.extendedInfo.name]
      ? html`<kat-expander
          label=${this.extendedInfoLabel ??
          getString('kat_hero_extended_info_label', undefined, this.locale)}
          ><slot name=${KatHero.slotMapping.extendedInfo.name}></slot
        ></kat-expander>`
      : nothing;
    const renderedListActions = slots[KatHero.slotMapping.listActions.name]
      ? html`<div class="list-actions">
          <slot name=${KatHero.slotMapping.listActions.name}></slot>
        </div>`
      : nothing;
    const renderedActions = hasActions
      ? html`<div class="actions">
          <slot name=${KatHero.slotMapping.actions.name}></slot>
        </div>`
      : nothing;

    const layoutClassMap = classMap({
      layout: true,
      'has-image': hasImage,
      'has-actions': hasActions,
    });

    return html`<header>
      ${renderedListActions}
      <div class="${layoutClassMap}">
        ${renderedImage}
        <div class="content">
          ${renderedHeader}
          <slot name=${KatHero.slotMapping.status.name}></slot>
          ${renderedSubheader} ${renderedPrice}
          <slot name=${KatHero.slotMapping.details.name}></slot>
          ${renderedExtendedInfo}
        </div>
        ${renderedActions}
      </div>
      <slot name=${KatHero.slotMapping.sectionHeaderGroup.name}></slot>
    </header>`;
  }
}
